import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useReportTimezone } from "@/lib/services/workspace-service";
import { DateRange } from "@gen/tracker/v1/common_pb";
import {
  compareRange,
  isDatePreset,
  parseBasis,
  parseCompare,
  parseScope,
  resolveRange,
  type CompareMode,
  type ConversionBasis,
  type DatePreset,
  type DayRange,
  type FixedPreset,
  type ResolvedRange,
  type Scope,
} from "@/lib/period";

export { isDatePreset };

// URL-search-param state for every screen under the Top bar: the period
// (?preset=7d or ?preset=custom&from=…&to=…), what it is compared against
// (?cmp), which date a conversion counts on (?by), whose data (?who), the
// saved view, plus arbitrary string filter keys (?campaign=…&country=…).
// Keeping the state in the URL makes every view shareable and survives
// reloads for free. The vocabulary is `lib/period`.

// The keys `filters` must never carry into an RPC as a dimension filter.
// `useTableParams` layers page/size/sort/q/cols/set on the same URL and they
// are listed here too — which hook reads a reserved key is beside the point.
const RESERVED_KEYS = new Set([
  "from",
  "to",
  "preset",
  // The zone is the workspace's (useReportTimezone); an old link's ?tz is
  // ignored, never read as a dimension filter.
  "tz",
  "cmp",
  "cmp_from",
  "cmp_to",
  "by",
  "who",
  "view",
  "page",
  "size",
  "sort",
  "q",
  "cols",
  "set",
]);

/** True for a key one of the two param hooks owns; `filters` never carries one. */
export function isReservedParamKey(key: string): boolean {
  return RESERVED_KEYS.has(key);
}

export type ResolvedReportRange = ResolvedRange;

/** Kept under its old name for the screens that resolve a range by hand. Pure. */
export function resolveReportRange(
  raw: { preset?: string | null; from?: string | null; to?: string | null },
  now: Date = new Date(),
  fallbackPreset: FixedPreset = "7d",
): ResolvedReportRange {
  return resolveRange(raw, now, fallbackPreset);
}

/** Non-reserved search params as a plain string map. Pure, for testing. */
export function filtersFromSearchParams(sp: URLSearchParams): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of sp.entries()) {
    if (!RESERVED_KEYS.has(k) && v !== "") out[k] = v;
  }
  return out;
}

export interface ReportParams {
  /** Effective preset (never an invalid value). */
  preset: DatePreset;
  /** ISO YYYY-MM-DD bounds, both inclusive. */
  from: string;
  to: string;
  /** The zone every day boundary is drawn in — the workspace's. */
  tz: string;
  /** What the deltas compare against. */
  compare: CompareMode;
  /** The comparison window, or undefined when `compare` is off. */
  compareRange: DayRange | undefined;
  /** Which date a conversion counts on. */
  basis: ConversionBasis;
  /** Whose data, on screens that offer the switch. */
  scope: Scope;
  /** Selected saved-report-view id ("" = resolve the schema default). */
  view: string;
  /** The protobuf DateRange for RPC calls — stable per from/to pair. */
  range: DateRange;
  /** Arbitrary non-reserved string filters present in the URL. */
  filters: Record<string, string>;
  setPreset: (preset: FixedPreset) => void;
  /** Switches to preset=custom with explicit bounds. */
  setRange: (range: DayRange) => void;
  /** The Top bar's «Применить»: period, comparison and basis in ONE write. `compareFrom`/`compareTo` only for `custom`. */
  setPeriod: (next: { preset: DatePreset; from: string; to: string; compare: CompareMode; compareFrom?: string; compareTo?: string; basis: ConversionBasis }) => void;
  setScope: (scope: Scope) => void;
  /**
   * Selects a saved view (null/"" clears back to the default) and drops any
   * per-key overrides so the view's definition becomes the effective state.
   */
  setView: (id: string | null, clearKeys?: string[]) => void;
  /** Set or clear (null/"" deletes) a filter key. Reserved keys are rejected. */
  setFilter: (key: string, value: string | null) => void;
  /**
   * Set or clear several filter keys in ONE navigation.
   *
   * Two `setFilter` calls in the same handler do not compose: react-router
   * builds the next params from the snapshot of the render the callback was
   * created in, so the second write starts from the old URL and drops the
   * first. A stats preset writes `dims` and `cols` together, and writing them
   * apart silently kept the previous columns.
   */
  setFilters: (patch: Record<string, string | null>) => void;
  getFilter: (key: string) => string;
}

export interface UseReportParamsOptions {
  /** Preset used when the URL has none / an invalid one. Default "7d". */
  defaultPreset?: FixedPreset;
}

export function useReportParams(options: UseReportParamsOptions = {}): ReportParams {
  const { defaultPreset = "7d" } = options;
  const [searchParams, setSearchParams] = useSearchParams();
  const tz = useReportTimezone();

  const rawPreset = searchParams.get("preset");
  const rawFrom = searchParams.get("from");
  const rawTo = searchParams.get("to");

  const { preset, from, to } = useMemo(
    () => resolveRange({ preset: rawPreset, from: rawFrom, to: rawTo }, new Date(), defaultPreset, tz),
    [rawPreset, rawFrom, rawTo, defaultPreset, tz],
  );

  const compare = parseCompare(searchParams.get("cmp"));
  const basis = parseBasis(searchParams.get("by"));
  const scope = parseScope(searchParams.get("who"));
  const view = searchParams.get("view") ?? "";

  const cmpFrom = searchParams.get("cmp_from") ?? "";
  const cmpTo = searchParams.get("cmp_to") ?? "";
  const range = useMemo(() => new DateRange({ from, to }), [from, to]);
  const cmpRange = useMemo(() => compareRange({ from, to }, compare, { from: cmpFrom, to: cmpTo }), [from, to, compare, cmpFrom, cmpTo]);

  const filters = useMemo(() => filtersFromSearchParams(searchParams), [searchParams]);

  const write = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setPreset = useCallback(
    (p: FixedPreset) =>
      write((next) => {
        next.set("preset", p);
        next.delete("from");
        next.delete("to");
      }),
    [write],
  );

  const setRange = useCallback(
    (r: DayRange) =>
      write((next) => {
        next.set("preset", "custom");
        next.set("from", r.from);
        next.set("to", r.to);
      }),
    [write],
  );

  const setPeriod = useCallback<ReportParams["setPeriod"]>(
    (p) =>
      write((next) => {
        next.set("preset", p.preset);
        if (p.preset === "custom") {
          next.set("from", p.from);
          next.set("to", p.to);
        } else {
          next.delete("from");
          next.delete("to");
        }
        // Defaults stay out of the address: a clean link is the default view.
        if (p.compare === "previous") next.delete("cmp");
        else next.set("cmp", p.compare);
        if (p.compare === "custom" && p.compareFrom && p.compareTo) {
          next.set("cmp_from", p.compareFrom);
          next.set("cmp_to", p.compareTo);
        } else {
          next.delete("cmp_from");
          next.delete("cmp_to");
        }
        if (p.basis === "lead") next.delete("by");
        else next.set("by", p.basis);
      }),
    [write],
  );

  const setScope = useCallback(
    (s: Scope) =>
      write((next) => {
        if (s === "all") next.delete("who");
        else next.set("who", s);
        // A different set of campaigns is a different list: back to page 1.
        next.delete("page");
      }),
    [write],
  );

  const setView = useCallback(
    (id: string | null, clearKeys: string[] = []) =>
      write((next) => {
        if (id) next.set("view", id);
        else next.delete("view");
        for (const k of clearKeys) next.delete(k);
      }),
    [write],
  );

  const setFilter = useCallback(
    (key: string, value: string | null) => {
      if (RESERVED_KEYS.has(key)) {
        throw new Error(`useReportParams: "${key}" is a reserved param`);
      }
      write((next) => {
        if (value) next.set(key, value);
        else next.delete(key);
      });
    },
    [write],
  );

  const setFilters = useCallback(
    (patch: Record<string, string | null>) => {
      for (const key of Object.keys(patch)) {
        if (RESERVED_KEYS.has(key)) {
          throw new Error(`useReportParams: "${key}" is a reserved param`);
        }
      }
      write((next) => {
        for (const [key, value] of Object.entries(patch)) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
      });
    },
    [write],
  );

  const getFilter = useCallback(
    (key: string) => (RESERVED_KEYS.has(key) ? "" : (searchParams.get(key) ?? "")),
    [searchParams],
  );

  return {
    preset,
    from,
    to,
    tz,
    compare,
    compareRange: cmpRange,
    basis,
    scope,
    view,
    range,
    filters,
    setPreset,
    setRange,
    setPeriod,
    setScope,
    setView,
    setFilter,
    setFilters,
    getFilter,
  };
}
